import { useState, useCallback } from "react";
import { Input } from "@aidkitorg/component-library";
import { usePost } from "../../../API";
import { useDebouncedEffect } from "../../../Hooks/Debounce";
import { useConnectionModal } from "./ConnectionModalContext";
import { type DeckSource } from "./types";
import { SourceLogo } from "./SourceLogo";

const DECK_MIN_SEARCH_STRING_LENGTH = 3;
const NUMBER_OF_SOURCES_TO_SHOW_AT_ONCE = 5;

export function ConnectionModalSearchPage() {
  const { getLocalizedString, onUpdateState } = useConnectionModal();
  const getSources = usePost("/deck/get_sources");

  const [searchString, setSearchString] = useState("");
  const [sources, setSources] = useState<DeckSource[] | null>(null);

  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchString(e.currentTarget.value);
  }, []);

  const handleSelectSource = useCallback((selectedSource: DeckSource) => {
    onUpdateState({ stage: "source", selectedSource });
  }, [onUpdateState]);

  useDebouncedEffect(async () => {
    if (searchString.length < DECK_MIN_SEARCH_STRING_LENGTH) {
      setSources(null);
      return;
    }

    const sources = await getSources({ search: searchString })
    setSources(sources.slice(0, NUMBER_OF_SOURCES_TO_SHOW_AT_ONCE));
  }, {
    timeWindow: 150,
    maxWait: 500,
    dependencies: [searchString],
  });

  return <div>
    <Input
      label={getLocalizedString("searchBarLabel", "search_bar_label")}
      value={searchString}
      placeholder={getLocalizedString("searchBarPlaceholder", "search_bar_placeholder")}
      onChange={handleSearchChange}
      dir="ltr" // The sources are all in English
    />

    {sources?.length === 0 &&
      <div className="mt-4">
        {getLocalizedString("noSearchResultsText", "no_search_results")}
      </div>
    }
    {!!sources?.length && sources.map((source) =>
      <div
        key={source.id}
        className="flex mt-3 cursor-pointer rounded-md hover:bg-gray-50 font-semibold text-gray-900 border-gray-100 border p-2 items-center"
        onClick={() => handleSelectSource(source)}
        dir="ltr" // The results from Deck will always be in English
      >
        <SourceLogo logoUrl={source.logo_url} size="small" />
        <span className="ml-4">{source.name}</span>
      </div>
    )}
  </div>
}
